import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.10thirty.co.za';

/**
 * Admin API request — uses cookie auth (session) with Bearer token fallback.
 */
async function adminRequest(endpoint, options = {}) {
  const token = sessionStorage.getItem('tt_admin_token');
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Request failed' }));
    if (response.status === 401) {
      sessionStorage.removeItem('tt_admin_token');
    }
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

function Admin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [activity, setActivity] = useState([]);
  const [tab, setTab] = useState('overview');

  // Sub-views
  const [users, setUsers] = useState(null);
  const [payments, setPayments] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [seats, setSeats] = useState(null);
  const [partners, setPartners] = useState(null);

  // Search/filters
  const [userSearch, setUserSearch] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');

  // Per-user UI state for partner tag controls.
  const [partnerActionUser, setPartnerActionUser] = useState(null);
  const [partnerPlanChoice, setPartnerPlanChoice] = useState('pro');

  // Check existing session on load
  useEffect(() => {
    const checkSession = async () => {
      try {
        const me = await adminRequest('/api/admin/me');
        setAdminUser(me);
        setAuthenticated(true);
        // Load dashboard data
        const [dashData, actData] = await Promise.all([
          adminRequest('/api/admin/dashboard'),
          adminRequest('/api/admin/activity'),
        ]);
        setDashboard(dashData);
        setActivity(actData.events || []);
      } catch {
        setAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, []);

  const login = useCallback(async () => {
    if (!email.trim() || !password.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Store token for Bearer auth fallback
      sessionStorage.setItem('tt_admin_token', data.token);
      setAdminUser(data.admin);
      setAuthenticated(true);

      // Load dashboard
      const [dashData, actData] = await Promise.all([
        adminRequest('/api/admin/dashboard'),
        adminRequest('/api/admin/activity'),
      ]);
      setDashboard(dashData);
      setActivity(actData.events || []);
    } catch (err) {
      setError(err.message);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [email, password]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [dashData, actData] = await Promise.all([
        adminRequest('/api/admin/dashboard'),
        adminRequest('/api/admin/activity'),
      ]);
      setDashboard(dashData);
      setActivity(actData.events || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async (search = '') => {
    try {
      const params = new URLSearchParams({ limit: '50', offset: '0' });
      if (search) params.set('search', search);
      const data = await adminRequest(`/api/admin/users?${params}`);
      setUsers(data);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const loadPayments = useCallback(async (status = '') => {
    try {
      const params = new URLSearchParams({ limit: '50', offset: '0' });
      if (status) params.set('status', status);
      const data = await adminRequest(`/api/admin/payments?${params}`);
      setPayments(data);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const loadSubscriptions = useCallback(async () => {
    try {
      const data = await adminRequest('/api/admin/subscriptions?limit=50');
      setSubscriptions(data);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const loadReferrals = useCallback(async () => {
    try {
      const data = await adminRequest('/api/admin/referrals?limit=50');
      setReferrals(data);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const loadSeats = useCallback(async () => {
    try {
      const data = await adminRequest('/api/admin/seats?limit=50');
      setSeats(data);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const loadPartners = useCallback(async () => {
    try {
      const data = await adminRequest('/api/admin/partners?limit=100');
      setPartners(data);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const tagUserAsPartner = useCallback(async (userId, plan) => {
    try {
      await adminRequest(`/api/admin/users/${userId}/partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });
      setPartnerActionUser(null);
      // Refresh whichever view the admin is on.
      if (tab === 'users') await loadUsers(userSearch);
      if (tab === 'partners') await loadPartners();
    } catch (err) {
      setError(err.message);
    }
  }, [tab, userSearch, loadUsers, loadPartners]);

  const untagPartner = useCallback(async (userId) => {
    if (!window.confirm('Untag this partner? Their comp plan reverts to free; existing commissions are kept.')) return;
    try {
      await adminRequest(`/api/admin/users/${userId}/partner`, { method: 'DELETE' });
      if (tab === 'users') await loadUsers(userSearch);
      if (tab === 'partners') await loadPartners();
    } catch (err) {
      setError(err.message);
    }
  }, [tab, userSearch, loadUsers, loadPartners]);

  useEffect(() => {
    if (!authenticated) return;
    if (tab === 'users' && !users) loadUsers();
    if (tab === 'payments' && !payments) loadPayments();
    if (tab === 'subscriptions' && !subscriptions) loadSubscriptions();
    if (tab === 'referrals' && !referrals) loadReferrals();
    if (tab === 'seats' && !seats) loadSeats();
    if (tab === 'partners' && !partners) loadPartners();
  }, [tab, authenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    try {
      const token = sessionStorage.getItem('tt_admin_token');
      await fetch(`${API_BASE}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
    } catch { /* ignore */ }
    sessionStorage.removeItem('tt_admin_token');
    setAuthenticated(false);
    setAdminUser(null);
    setDashboard(null);
  };

  const formatUSD = (cents) => `$${(cents / 100).toFixed(2)}`;
  const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return d.toLocaleDateString();
  };

  // Loading check
  if (loading && !authenticated && !error) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <img src="/logo192.png" alt="10 Thirty" style={{ width: 48, marginBottom: 16 }} />
          <p style={{ color: '#71717A' }}>Checking session...</p>
        </div>
      </div>
    );
  }

  // Login screen
  if (!authenticated) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <img src="/logo192.png" alt="10 Thirty" style={{ width: 48, marginBottom: 16 }} />
          <h1>10 Thirty Admin</h1>
          <p>Sign in to access the dashboard.</p>
          {error && <div className="alert alert-error">{error}</div>}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && document.getElementById('admin-pw')?.focus()}
            placeholder="Email"
            className="admin-input"
            autoComplete="email"
          />
          <input
            id="admin-pw"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && login()}
            placeholder="Password"
            className="admin-input"
            autoComplete="current-password"
          />
          <button onClick={login} disabled={loading} className="btn-primary" style={{ width: '100%' }}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </div>
      </div>
    );
  }

  const d = dashboard;

  return (
    <div className="admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <img src="/logo192.png" alt="10 Thirty" style={{ width: 32 }} />
          <h1>Admin Dashboard</h1>
        </div>
        <div className="admin-header-right">
          {adminUser && <span className="dim">{adminUser.email || adminUser.name}</span>}
          <button onClick={refresh} disabled={loading} className="btn-secondary btn-small">
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button onClick={handleLogout} className="btn-secondary btn-small">
            Logout
          </button>
        </div>
      </header>

      {error && <div className="alert alert-error" style={{ margin: '0 24px 16px' }}>{error}</div>}

      {/* Tabs */}
      <nav className="admin-tabs">
        {['overview', 'users', 'payments', 'subscriptions', 'referrals', 'partners', 'seats'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`admin-tab ${tab === t ? 'active' : ''}`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </nav>

      <div className="admin-content">
        {/* ========== OVERVIEW TAB ========== */}
        {tab === 'overview' && d && (
          <>
            {/* Stat Cards */}
            <div className="stat-grid">
              <div className="stat-card">
                <div className="stat-value">{d.users.total}</div>
                <div className="stat-label">Total Users</div>
                <div className="stat-sub">+{d.users.today} today / +{d.users.this_week} this week</div>
              </div>
              <div className="stat-card accent">
                <div className="stat-value">{d.subscriptions.total_paying}</div>
                <div className="stat-label">Paying Users</div>
                <div className="stat-sub">{d.subscriptions.pro} Pro / {d.subscriptions.business} Business</div>
              </div>
              <div className="stat-card success">
                <div className="stat-value">{d.revenue.this_month_formatted}</div>
                <div className="stat-label">Revenue (30d)</div>
                <div className="stat-sub">{d.revenue.total_formatted} all time</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{d.users.online_now}</div>
                <div className="stat-label">Online Now</div>
                <div className="stat-sub">{d.tunnels.active_now} active tunnels</div>
              </div>
            </div>

            {/* Secondary Stats */}
            <div className="stat-grid">
              <div className="stat-card">
                <div className="stat-value">{d.tunnels.today}</div>
                <div className="stat-label">Tunnels Today</div>
                <div className="stat-sub">{d.tunnels.this_week} this week / {d.tunnels.total} total</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{d.messages.today}</div>
                <div className="stat-label">Messages Today</div>
                <div className="stat-sub">{d.messages.this_week} this week</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{d.contacts.total_accepted}</div>
                <div className="stat-label">Connections</div>
                <div className="stat-sub">{d.contacts.pending_requests} pending requests</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{d.referrals.total}</div>
                <div className="stat-label">Referrals</div>
                <div className="stat-sub">{d.payments.pending} pending / {d.payments.failed} failed payments</div>
              </div>
            </div>

            {/* Activity Feed */}
            <div className="admin-section">
              <h2>Recent Activity</h2>
              <div className="activity-feed">
                {activity.length === 0 ? (
                  <p className="empty-state">No recent activity</p>
                ) : (
                  activity.slice(0, 20).map((event, i) => (
                    <div key={i} className="activity-item">
                      <span className={`activity-dot ${event.type}`} />
                      <span className="activity-text">{event.description}</span>
                      <span className="activity-time">{formatTime(event.timestamp)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* ========== USERS TAB ========== */}
        {tab === 'users' && (
          <div className="admin-section">
            <div className="admin-toolbar">
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadUsers(userSearch)}
                placeholder="Search by phone or name..."
                className="admin-input"
              />
              <button onClick={() => loadUsers(userSearch)} className="btn-primary btn-small">Search</button>
            </div>
            {users && (
              <>
                <p className="table-meta">{users.total} users total</p>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Plan</th>
                        <th>Partner</th>
                        <th>Credits</th>
                        <th>Status</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.users.map((u) => (
                        <tr key={u.id}>
                          <td>{u.name || '—'}</td>
                          <td className="mono">{u.phone}</td>
                          <td>
                            <span className={`plan-badge ${u.plan}`}>{u.plan}</span>
                            {u.subscription_source === 'partner_comp' && (
                              <span className="comp-tag" title="Comped via partner program">comp</span>
                            )}
                          </td>
                          <td>
                            {u.partner_status === 'active' ? (
                              <div className="partner-cell">
                                <span className="partner-status-pill active">Partner</span>
                                <button
                                  className="btn-link-danger"
                                  onClick={() => untagPartner(u.id)}
                                >
                                  Untag
                                </button>
                              </div>
                            ) : partnerActionUser === u.id ? (
                              <div className="partner-cell">
                                <select
                                  value={partnerPlanChoice}
                                  onChange={(e) => setPartnerPlanChoice(e.target.value)}
                                  className="admin-select admin-select-sm"
                                >
                                  <option value="pro">Pro</option>
                                  <option value="business">Business</option>
                                </select>
                                <button
                                  className="btn-primary btn-small"
                                  onClick={() => tagUserAsPartner(u.id, partnerPlanChoice)}
                                >
                                  Tag
                                </button>
                                <button
                                  className="btn-link"
                                  onClick={() => setPartnerActionUser(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                className="btn-link"
                                onClick={() => { setPartnerActionUser(u.id); setPartnerPlanChoice('pro'); }}
                              >
                                Tag…
                              </button>
                            )}
                          </td>
                          <td>{u.credits}</td>
                          <td>{u.is_online ? <span className="online-dot" /> : <span className="offline-dot" />}</td>
                          <td className="dim">{formatTime(u.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ========== PAYMENTS TAB ========== */}
        {tab === 'payments' && (
          <div className="admin-section">
            <div className="admin-toolbar">
              <select
                value={paymentStatus}
                onChange={(e) => { setPaymentStatus(e.target.value); loadPayments(e.target.value); }}
                className="admin-select"
              >
                <option value="">All statuses</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            {payments && (
              <>
                <p className="table-meta">{payments.total} payments</p>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Description</th>
                        <th>Date</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.payments.map((p) => (
                        <tr key={p.id}>
                          <td>{p.user_name || p.user_phone}</td>
                          <td>{p.payment_type}</td>
                          <td className="mono">{p.amount_usd_cents ? formatUSD(p.amount_usd_cents) : `R${(p.amount_cents/100).toFixed(0)}`}</td>
                          <td><span className={`status-pill ${p.status}`}>{p.status}</span></td>
                          <td className="dim">{p.description || '—'}</td>
                          <td className="dim">{formatTime(p.created_at)}</td>
                          <td>
                            {(p.status === 'pending' || p.status === 'failed') && (
                              <button
                                className="btn-danger btn-small"
                                onClick={async () => {
                                  if (!window.confirm(`Delete this ${p.status} payment for ${p.user_name || p.user_phone}?`)) return;
                                  try {
                                    await adminRequest(`/api/admin/payments/${p.id}`, { method: 'DELETE' });
                                    loadPayments(paymentStatus);
                                  } catch (err) {
                                    setError(err.message);
                                  }
                                }}
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ========== SUBSCRIPTIONS TAB ========== */}
        {tab === 'subscriptions' && (
          <div className="admin-section">
            {subscriptions && (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Period End</th>
                      <th>Failed Attempts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.subscriptions.map((s) => (
                      <tr key={s.id}>
                        <td>{s.user_name || s.user_phone}</td>
                        <td><span className={`plan-badge ${s.plan}`}>{s.plan}</span></td>
                        <td><span className={`status-pill ${s.status}`}>{s.status}</span></td>
                        <td className="mono">{s.amount_usd_cents ? formatUSD(s.amount_usd_cents) : '—'}</td>
                        <td className="dim">{s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : '—'}</td>
                        <td>{s.failed_payment_attempts > 0 ? <span className="warn">{s.failed_payment_attempts}</span> : '0'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ========== REFERRALS TAB ========== */}
        {tab === 'referrals' && (
          <div className="admin-section">
            {referrals && (
              <>
                <p className="table-meta">{referrals.total} referrals</p>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Referrer</th>
                        <th>Referee</th>
                        <th>Code</th>
                        <th>Bonus</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.referrals.map((r) => (
                        <tr key={r.id}>
                          <td>{r.referrer_name || r.referrer_phone}</td>
                          <td>{r.referee_name || r.referee_phone}</td>
                          <td className="mono">{r.referral_code}</td>
                          <td>{r.bonus_credited ? <span className="success-text">Credited</span> : 'Pending'}</td>
                          <td className="dim">{formatTime(r.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ========== PARTNERS TAB ========== */}
        {tab === 'partners' && (
          <div className="admin-section">
            {partners && (
              <>
                <p className="table-meta">{partners.total} active partner{partners.total === 1 ? '' : 's'}</p>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Tier</th>
                        <th>Rate</th>
                        <th>Active Paying</th>
                        <th>Next Tier</th>
                        <th>Tagged</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {partners.partners.map((p) => (
                        <tr key={p.user_id}>
                          <td>{p.name || '—'}</td>
                          <td className="mono">{p.phone}</td>
                          <td><span className={`tier-pill tier-${p.tier}`}>{p.tier}</span></td>
                          <td className="mono">{(p.rate_bps / 100).toFixed(0)}%</td>
                          <td>{p.active_paying_referrals}</td>
                          <td className="dim">
                            {p.next_tier_in !== null
                              ? `${p.next_tier_in} more → ${p.next_tier_threshold}`
                              : 'Maxed'}
                          </td>
                          <td className="dim">{formatTime(p.partner_tagged_at)}</td>
                          <td>
                            <button
                              className="btn-link-danger"
                              onClick={() => untagPartner(p.user_id)}
                            >
                              Untag
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {partners.total === 0 && (
                  <p className="empty-state">
                    No partners yet — tag a user from the Users tab to start.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* ========== SEATS TAB ========== */}
        {tab === 'seats' && (
          <div className="admin-section">
            {seats && (
              <>
                <p className="table-meta">{seats.total_active} active seats</p>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Owner</th>
                        <th>Member</th>
                        <th>Assigned Plan</th>
                        <th>Status</th>
                        <th>Prior Plan</th>
                        <th>Added</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seats.seats.map((s) => (
                        <tr key={s.id}>
                          <td>{s.owner_name || s.owner_phone}</td>
                          <td>{s.member_name || s.member_phone}</td>
                          <td><span className={`plan-badge ${s.assigned_plan}`}>{s.assigned_plan}</span></td>
                          <td><span className={`status-pill ${s.status}`}>{s.status}</span></td>
                          <td className="dim">{s.prior_plan || '—'}</td>
                          <td className="dim">{formatTime(s.added_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="admin-footer">
        <Link to="/">Back to Site</Link>
        {d && <span className="dim">Last updated: {formatTime(d.generated_at)}</span>}
      </footer>
    </div>
  );
}

export default Admin;
